import { supabase } from "../supabase/supabase";
import {
  Slot,
  SlotInsert,
  SlotUpdate,
  SlotSegment,
  SlotSegmentInsert,
  SlotSegmentUpdate,
  SlotBooking,
  SlotBookingInsert,
  SlotBookingUpdate,
} from "../types/slot";

interface SlotFilters {
  organizationId?: string;
  startDate?: string;
  endDate?: string;
}

export const slotService = {
  async getSlots(filters: SlotFilters) {
    try {
      // Fetch slots with their segments
      let query = supabase.from("slots").select(`
        *,
        segments:slot_segments(*)
      `);

      if (filters.organizationId) {
        query = query.eq("organization_id", filters.organizationId);
      }

      if (filters.startDate) {
        query = query.gte("start_time", filters.startDate);
      }

      if (filters.endDate) {
        query = query.lte("end_time", filters.endDate);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error("Error getting slots:", error);
      return { data: null, error };
    }
  },

  async getSlot(id: string) {
    try {
      // Fetch the slot with its segments
      const { data, error } = await supabase
        .from("slots")
        .select(`
          *,
          segments:slot_segments(*)
        `)
        .eq("id", id)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error("Error getting slot:", error);
      return { data: null, error };
    }
  },

  async createSlot(slotData: SlotInsert) {
    try {
      const { data, error } = await supabase
        .from("slots")
        .insert({
          organization_id: slotData.organization_id,
          name: slotData.name,
          description: slotData.description,
          start_time: slotData.start_time,
          end_time: slotData.end_time,
          time_limit: slotData.time_limit,
          status: slotData.status,
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error("Error creating slot:", error);
      return { data: null, error };
    }
  },

  // ponytail: placeholder that returns only the base slot; expand by pattern when recurrence is built
  generateRecurringSlots(baseSlot: SlotInsert, _pattern: string, _endDate: string): SlotInsert[] {
    return [baseSlot];
  },

  async createMultipleSlots(slots: SlotInsert[]) {
    try {
      const { data, error } = await supabase
        .from("slots")
        .insert(slots)
        .select();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error("Error creating multiple slots:", error);
      return { data: null, error };
    }
  },

  async updateSlot(id: string, updates: SlotUpdate) {
    try {
      const { data, error } = await supabase
        .from("slots")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error("Error updating slot:", error);
      return { data: null, error };
    }
  },

  async deleteSlot(id: string) {
    try {
      const { error } = await supabase.from("slots").delete().eq("id", id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error("Error deleting slot:", error);
      return { error };
    }
  },

  // Slot segments
  async getSlotSegments(slotId: string) {
    try {
      const { data, error } = await supabase
        .from("slot_segments")
        .select("*")
        .eq("slot_id", slotId);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error("Error getting slot segments:", error);
      return { data: null, error };
    }
  },

  async createSlotSegment(segmentData: SlotSegmentInsert) {
    try {
      const { data, error } = await supabase
        .from("slot_segments")
        .insert(segmentData)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error("Error creating slot segment:", error);
      return { data: null, error };
    }
  },

  async updateSlotSegment(id: string, updates: SlotSegmentUpdate) {
    try {
      const { data, error } = await supabase
        .from("slot_segments")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error("Error updating slot segment:", error);
      return { data: null, error };
    }
  },

  async deleteSlotSegment(id: string) {
    try {
      const { error } = await supabase
        .from("slot_segments")
        .delete()
        .eq("id", id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error("Error deleting slot segment:", error);
      return { error };
    }
  },

  async deleteSlotSegments(slotId: string) {
    try {
      const { error } = await supabase
        .from("slot_segments")
        .delete()
        .eq("slot_id", slotId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error("Error deleting slot segments:", error);
      return { error };
    }
  },

  // Slot bookings
  async getSlotBookings(slotId: string) {
    try {
      const { data, error } = await supabase
        .from("slot_bookings")
        .select("*")
        .eq("slot_id", slotId);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error("Error getting slot bookings:", error);
      return { data: null, error };
    }
  },

  async createBooking(bookingData: SlotBookingInsert) {
    try {
      const { data, error } = await supabase
        .from("slot_bookings")
        .insert(bookingData)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error("Error creating booking:", error);
      return { data: null, error };
    }
  },

  async updateBooking(id: string, updates: SlotBookingUpdate) {
    try {
      const { data, error } = await supabase
        .from("slot_bookings")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error("Error updating booking:", error);
      return { data: null, error };
    }
  },

  async deleteBooking(id: string) {
    try {
      const { error } = await supabase
        .from("slot_bookings")
        .delete()
        .eq("id", id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error("Error deleting booking:", error);
      return { error };
    }
  },
};
